import * as React from "react";
import { observer } from "mobx-react-lite";
import { classNames } from "src/common/classNames";
import { Button, Spin } from "antd";
import { PlusOutlined, UpOutlined } from "@ant-design/icons";
import { ZUid } from "src/types/zUid";
import { ResizerStd } from "src/pages/EntityFiltersPage/AsyncTableView/ResizerStd";
import { toggleOverlapClass } from "src/common/toggleOverlapClass";
import { ZBom3PosRow } from "../ZBom3Position";
import {
  Bom3PoitionSelectMaterial,
  Bom3PositionSelectHead,
  Bom3PositionSelectRow,
} from "./Bom3PositionSelect";
import { bom3PosFirstColWidth, Bom3PositionStore } from "../Bom3PositionsStore";
import styles from "./Bom3PositionsTable.module.less";
import { Bom3PosColController } from "../Bom3PosColumn";

type PropsBom3PositionsTable = {
  store: Bom3PositionStore;
};

export const Bom3PositionsTable: React.FC<PropsBom3PositionsTable> = observer(
  ({ store }) => {
    const {
      columns,
      columnWidthList,
      gridTemplateColumns,
      totalColumnsWidth,
      collapsedMaterials,
      materials,
      buzy,
      visibleFixedCount,
    } = store;
    const refTable = React.useRef<HTMLDivElement>(null);
    const refHeader = React.useRef<HTMLDivElement>(null);
    const refTBodyBox = React.useRef<HTMLDivElement>(null);
    const fixedColumnsExt = visibleFixedCount + 1; // учитывается колонка с чекбоксами

    React.useEffect(() => {
      store.sheetStore.init();
    }, []);

    React.useEffect(() => () => {
      store.sheetStore.postUpdate();
    });

    let rowIndex = -1;
    let prevRowId: ZUid = "";
    const getRowIndex = (row: ZBom3PosRow): number => {
      if (row.rowId !== prevRowId) {
        rowIndex++;
        prevRowId = row.rowId;
      }
      return rowIndex;
    };
    let lastX = 0;
    const getX = (colIndex: number, isCell: boolean): number => {
      if (colIndex === 0) lastX = 0;
      return isCell ? lastX++ : lastX;
    };

    const clsFixed = (colIndex: number): (string | undefined)[] => [
      colIndex < fixedColumnsExt ? styles.fixedCell : undefined,
      colIndex === fixedColumnsExt - 1 ? styles.fixedLastCell : undefined,
    ];
    const leftOffset = (colIndex: number): number => {
      let offset = 0;
      let i = 0;
      while (i < colIndex && i < columnWidthList.length) {
        offset += columnWidthList[i] ?? 0;
        i += 1;
      }
      return offset;
    };
    const leftPos = (pureColIndex: number): number | undefined => {
      const colIndex = pureColIndex + 1;
      if (colIndex >= fixedColumnsExt) {
        return undefined;
      }
      return leftOffset(colIndex);
    };

    const columnController: Bom3PosColController = {
      onFocus(target) {
        if (!(target instanceof HTMLElement)) return;
        const cell = target.closest(`[data-colIndex]`);
        if (!(cell instanceof HTMLElement)) return;
        const sColIndex = cell.getAttribute("data-colIndex");
        if (typeof sColIndex !== "string") return;
        const colIndex = +sColIndex;
        if (Number.isNaN(colIndex)) return;

        // если ячейка в фиксированной области, то скроллировать не надо (LPLM-5891)
        if (colIndex <= visibleFixedCount) return;

        const scrollLeft = refTBodyBox.current?.scrollLeft;
        if (typeof scrollLeft !== "number") return;
        const curColumnLeft = leftOffset(colIndex);
        const fixedRight = leftOffset(visibleFixedCount + 1);
        if (curColumnLeft - scrollLeft < fixedRight) {
          refTBodyBox.current!.scrollLeft = curColumnLeft - fixedRight;
        }
      },
    };

    return (
      <div className={styles.main}>
        <div ref={refTable} className={styles.table}>
          <div
            className={styles.thead}
            style={{ gridTemplateColumns }}
            ref={refHeader}
          >
            <div
              className={classNames([styles.cellFirst, ...clsFixed(0)])}
              style={{ left: 0 }}
            >
              <Bom3PositionSelectHead store={store} />
            </div>
            {columns.map(({ key, header }, colIndex) => (
              <div
                key={key}
                className={classNames([styles.th, ...clsFixed(colIndex + 1)])}
                style={{ left: leftPos(colIndex) }}
              >
                <div className={styles.thTitle}>{header}</div>
                <ResizerStd
                  column={{ key: String(key) }}
                  store={store.ctrlResizable}
                  minWidth={Bom3PositionStore.minWidth}
                />
              </div>
            ))}
          </div>
          <div
            className={styles.tbodyBox}
            ref={refTBodyBox}
            onScroll={({ currentTarget: { scrollLeft } }) => {
              if (refHeader.current) {
                refHeader.current.scrollLeft = scrollLeft;
              }
              toggleOverlapClass(
                refTable.current,
                scrollLeft,
                styles.fixedLastCell,
                styles.fixedOverlap,
              );
            }}
          >
            <div
              className={styles.tbody}
              style={{ gridTemplateColumns, width: totalColumnsWidth }}
            >
              {materials.map(({ sectionId, sectionName, rows }) => (
                <React.Fragment key={sectionId}>
                  <div
                    className={classNames([
                      styles.materialRow,
                      styles.cellFirst,
                      styles.fixedCell,
                    ])}
                    style={{ left: 0 }}
                  >
                    <Bom3PoitionSelectMaterial
                      store={store}
                      materialId={sectionId}
                    />
                  </div>
                  <div
                    className={classNames([
                      styles.materialRow,
                      styles.materialCell,
                      styles.fixedCell,
                    ])}
                    style={{
                      left: bom3PosFirstColWidth,
                    }}
                  >
                    {store.possibleCreate && (
                      <Button
                        icon={<PlusOutlined />}
                        type="primary"
                        size="small"
                        title="Создание пустой BOM position"
                        disabled={buzy}
                        onClick={() => store.createEmptyPosition(sectionId)}
                      />
                    )}
                    {rows.length === 0 ? (
                      <span className={styles.materialName}>{sectionName}</span>
                    ) : (
                      <Button
                        icon={<UpOutlined />}
                        type="text"
                        size="small"
                        className={classNames([
                          [collapsedMaterials.has(sectionId), styles.collapsed],
                        ])}
                        onClick={() => store.toggleMaterialCollapse(sectionId)}
                      >
                        <span className={styles.materialName}>
                          {sectionName} : {rows.length}
                        </span>
                      </Button>
                    )}
                  </div>

                  <div
                    style={{ gridColumn: `span ${columns.length - 1}` }}
                    className={styles.materialRow}
                  />

                  {!collapsedMaterials.has(sectionId) &&
                    rows.map((row) => (
                      <React.Fragment key={row.rowId}>
                        <div
                          className={classNames([
                            styles.cellFirst,
                            ...clsFixed(0),
                          ])}
                          style={{ left: 0 }}
                        >
                          <Bom3PositionSelectRow
                            store={store}
                            rowId={row.rowId}
                          />
                        </div>
                        {columns.map(
                          ({ key, renderCell, isCell }, colIndex) => (
                            <div
                              key={key}
                              className={classNames([
                                styles.td,
                                ...clsFixed(colIndex + 1),
                              ])}
                              style={{
                                left: leftPos(colIndex),
                              }}
                              data-colIndex={colIndex + 1}
                            >
                              {renderCell(
                                row,
                                getRowIndex(row),
                                getX(colIndex, isCell),
                                columnController,
                              )}
                            </div>
                          ),
                        )}
                      </React.Fragment>
                    ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
        {buzy && (
          <div className={styles.overlay}>
            <Spin size="large" />
          </div>
        )}
      </div>
    );
  },
);
